import { DeliveredType, LastQuote, RateForm, RateResult, QuoteHistoryItem } from "../types";
import { quoteRates } from "../api";
import { LynxPackField } from "./LynxPackField";
import { DeliveryTypeSelect } from "./DeliveryTypeSelect";
import Button from "../../../components/ui/Button";

type ManualQuotePanelProps = {
  form: RateForm;
  setForm: React.Dispatch<React.SetStateAction<RateForm>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  batchLoading: boolean;
  setError: React.Dispatch<React.SetStateAction<string>>;
  setResults: React.Dispatch<React.SetStateAction<RateResult[]>>;
  setHistory: React.Dispatch<React.SetStateAction<QuoteHistoryItem[]>>;
  // La última cotización guardada, para que la página pueda generar una
  // orden a partir de sus resultados (fase 6).
  setLastQuote?: React.Dispatch<React.SetStateAction<LastQuote | null>>;
};

export function ManualQuotePanel({
  form,
  setForm,
  loading,
  setLoading,
  batchLoading,
  setError,
  setResults,
  setHistory,
  setLastQuote,
}: ManualQuotePanelProps) {
  const canQuote =
    form.postalCodeOrigin.trim() &&
    form.postalCodeDestination.trim() &&
    form.weight.trim() &&
    form.height.trim() &&
    form.width.trim() &&
    form.length.trim() &&
    Number(form.declaredValue) > 0;

  const handleChange = (
    field: keyof RateForm,
    value: string | DeliveredType
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleQuote = async () => {
    setError("");
    setLoading(true);
    setResults([]);
    setLastQuote?.(null);

    try {
      const cotizacion = await quoteRates({
        postalCodeOrigin: form.postalCodeOrigin.trim(),
        postalCodeDestination: form.postalCodeDestination.trim(),
        deliveredType: form.deliveredType,
        weight: Number(form.weight),
        height: Number(form.height),
        width: Number(form.width),
        length: Number(form.length),
        declaredValue: Number(form.declaredValue),
        mode: "single",
      });

      setResults(cotizacion.rates);

      // Solo una cotización guardada (con id del servidor) puede volverse
      // orden: el precio se congela desde esa fila.
      if (cotizacion.id) {
        setLastQuote?.({
          id: cotizacion.id,
          createdAt: cotizacion.createdAt,
          postalCodeOrigin: form.postalCodeOrigin.trim(),
          postalCodeDestination: form.postalCodeDestination.trim(),
          declaredValue: cotizacion.declaredValue ?? Number(form.declaredValue),
          rates: cotizacion.rates,
          carrierErrors: cotizacion.carrierErrors,
        });
      }

      if (cotizacion.sinTarifas) {
        setError(
          "Ningún transportista ofrece servicio para ese bulto hacia ese destino."
        );
      }

      // El identificador, la fecha y las medidas son las que devolvió el
      // servidor, que es donde quedó guardada la cotización.
      setHistory((prev) => [
        {
          ...form,
          id: cotizacion.id ?? crypto.randomUUID(),
          createdAt: cotizacion.createdAt,
          results: cotizacion.rates,
          mode: "single",
          validTo: cotizacion.validTo,
          volumeCm3: cotizacion.volumeCm3,
          aforoKg: cotizacion.aforoKg,
          billableKg: cotizacion.billableKg,
          customerId: cotizacion.customerId,
          sinTarifas: cotizacion.sinTarifas,
          carrierErrors: cotizacion.carrierErrors,
        },
        ...prev,
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error inesperado.");
    } finally {
      setLoading(false);
    }
  };

  // Sin tarjeta propia: la página lo monta dentro del Card "Cotizar", que
  // alterna entre este panel y el masivo con un switch.
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: 13 }}>
        Probá una combinación puntual de origen, destino, tipo de entrega y paquete.
      </p>
        <LynxPackField label="CP origen" value={form.postalCodeOrigin} onChange={(v) => handleChange("postalCodeOrigin", v)} placeholder="Ej: 1407" />
        <LynxPackField label="CP destino" value={form.postalCodeDestination} onChange={(v) => handleChange("postalCodeDestination", v)} placeholder="Ej: 5000" />

        <DeliveryTypeSelect value={form.deliveredType} onChange={(v) => handleChange("deliveredType", v)} allowAll />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 }}>
          <LynxPackField label="Peso gramos" value={form.weight} onChange={(v) => handleChange("weight", v)} type="number" placeholder="1000" />
          <LynxPackField label="Alto cm" value={form.height} onChange={(v) => handleChange("height", v)} type="number" placeholder="10" />
          <LynxPackField label="Ancho cm" value={form.width} onChange={(v) => handleChange("width", v)} type="number" placeholder="20" />
          <LynxPackField label="Largo cm" value={form.length} onChange={(v) => handleChange("length", v)} type="number" placeholder="30" />
        </div>

        <div>
          <LynxPackField
            label="Valor declarado: precio del contenido ($)"
            value={form.declaredValue ?? ""}
            onChange={(v) => handleChange("declaredValue", v)}
            type="number"
            placeholder="Precio real de la mercadería"
          />
          <p style={{ margin: "4px 0 0", color: "var(--text-secondary)", fontSize: 12 }}>
            Va en la cotización porque cambia el precio (seguro del envío). Es la base de la
            indemnización ante pérdida o daño: declarar el valor real del contenido.
          </p>
        </div>

        <Button
          variant="primary"
          fullWidth
          onClick={handleQuote}
          disabled={!canQuote || loading || batchLoading}
        >
          {loading ? "Cotizando..." : "Cotizar"}
        </Button>
    </div>
  );
}